import {
  EDITOR_DIAMETER_INPUT_MAX,
  EDITOR_DIAMETER_INPUT_MIN,
  ProductTypes,
  USER_TYPES,
} from '@editor/config/constants'
import { Project } from '@editor/interfaces/project'
import { UserInfo } from '@editor/interfaces/user'
import { PermissionSets } from '@editor/interfaces/validation'

/**
 * サイズが最大サイズ以下であることをチェックする関数
 * @param {number} size チェック対象サイズ
 * @param {number} maxSize 最大サイズ
 * @return {boolean} 適合: true, 不適合:false
 */
export const checkSize = (size: number, maxSize: number) => size <= maxSize

const regex = /^(1?[0-9]{0,2}|0)(\.[0-9]{1,2})?$/
/**
 * 直径フォーマットチェック、正数かつ小数点2桁かつ0.01~100mmであること
 * @param {string} diameter 直径
 * @return {boolean} 適合: true, 不適合:false
 */
export const checkDiameterFormat = (diameter: string) => {
  //* 必須チェック
  if (!diameter) return false
  //* 桁数チェック
  if (!regex.test(diameter)) return false
  //* 境界値チェック
  const floatDiameter = parseFloat(diameter)
  if (floatDiameter < EDITOR_DIAMETER_INPUT_MIN || floatDiameter > EDITOR_DIAMETER_INPUT_MAX) return false
  return true
}

export const decideUserType = (userInfo: UserInfo): keyof typeof USER_TYPES => {
  // If user is admin, that's it.
  if (userInfo.owned_products.includes(ProductTypes.Admin)) {
    return USER_TYPES.ADMIN as keyof typeof USER_TYPES
  }

  // Only base is defined
  if (userInfo.owned_products.length === 1 && userInfo.owned_products[0] === ProductTypes.Base) {
    return USER_TYPES.BASE_USER as keyof typeof USER_TYPES
  }

  // Has either Hatsuly or Markly, they are paying users
  if (userInfo.owned_products.includes(ProductTypes.Hatsuly) || userInfo.owned_products.includes(ProductTypes.Markly)) {
    return USER_TYPES.PAYING_USER as keyof typeof USER_TYPES
  }

  return USER_TYPES.NON_PAYING_USER as keyof typeof USER_TYPES
}

/**
 * Decide the permission for some actions based on the user type
 * @param owner is the owner of the project/project group
 * @param invited is invited to the project/project group
 * @returns array of user types that allowed for some permissions
 */
export const decideActionPermission = (
  owner: boolean = false,
  invited: boolean = false,
  ownedProducts: ProductTypes[] | undefined = undefined,
): PermissionSets => {
  const hasMarkly = (ownedProducts || []).includes(ProductTypes.Markly)
  const hasHatsuly = (ownedProducts || []).includes(ProductTypes.Hatsuly)
  const canModifyEither =
    ((hasMarkly || hasHatsuly) && (owner === undefined || owner === true)) || (owner && ownedProducts === undefined)

  // pattern 1 (view/comment)
  const viewPattern = [
    ...(canModifyEither || owner || invited
      ? [USER_TYPES.PAYING_USER, USER_TYPES.BETA_USER, USER_TYPES.BASE_USER]
      : []),
    ...(invited ? [USER_TYPES.NON_PAYING_USER] : []),
    USER_TYPES.ADMIN,
  ]

  // pattern 2 (edit)
  const modifyPattern = canModifyEither ? [USER_TYPES.PAYING_USER, USER_TYPES.BETA_USER, USER_TYPES.ADMIN] : []

  // pattern 3 (edit comment)
  const editPattern = [
    ...(canModifyEither || invited ? [USER_TYPES.PAYING_USER, USER_TYPES.BETA_USER, USER_TYPES.BASE_USER] : []),
    ...(invited ? [USER_TYPES.NON_PAYING_USER] : []),
    ...(canModifyEither ? [USER_TYPES.ADMIN] : []),
  ]

  // pattern 4 (modify display settings - owner only, includes base_user)
  const modifyDisplaySettingsPattern = owner
    ? [USER_TYPES.PAYING_USER, USER_TYPES.BETA_USER, USER_TYPES.BASE_USER, USER_TYPES.ADMIN]
    : []

  return {
    PROJECT_DASHBOARD: {
      ACTIVATE_FEATURES: [USER_TYPES.NON_PAYING_USER, USER_TYPES.BASE_USER],
      CREATE_PROJECT_GROUP: [USER_TYPES.PAYING_USER, USER_TYPES.BETA_USER],
      INVITE_USER_TO_PROJECT: modifyPattern,
      INVITE_TO_CREATE_ACCOUNT: [USER_TYPES.PAYING_USER, USER_TYPES.BETA_USER],
      BROWSE: viewPattern,
      MODIFY: modifyPattern,
    },
    EDITOR: {
      BROWSE: viewPattern,
      MODIFY: modifyPattern,
      MODIFY_DISPLAY_SETTINGS: modifyDisplaySettingsPattern,
    },
    INSPECTION_SHEET: {
      BROWSE: viewPattern,
      DOWNLOAD: viewPattern,
      MODIFY: modifyPattern,
    },
    BLUEPRINT: {
      BROWSE: viewPattern,
      MODIFY: hasMarkly ? modifyPattern : [],
    },
    COMMENT_THREAD: {
      BROWSE: viewPattern,
      CREATE: viewPattern,
      EDIT: editPattern,
    },
  }
}

/**
 * Check if the user is the owner of the project
 *
 * @param project Project to check
 * @param userProfile User profile
 */
export const isOwner = (project: Project, userProfile: UserInfo): boolean => project.user_id === userProfile.user_id

/**
 * Check if the user is invited to the project
 *
 * @param project Project to check
 * @param userProfile User profile
 */
export const isInvited = (project: Project, userProfile: UserInfo): boolean =>
  project.shared_user ? !!project.shared_user[userProfile.user_id] : false
